// library imports
import {
  CopyOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FileOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useRef, useState, useEffect } from "react";
import MarkdownIt from "markdown-it";
import { Actions, Attachments, Bubble, Welcome } from "@ant-design/x";
import {
  Button,
  Card,
  Flex,
  Image,
  Space,
  theme,
  Tooltip,
  Typography,
} from "antd";
// function imports
import { useShowMessage } from "../../../functions/Common/showMessage";
import useSendFile from "../../../functions/FileOperation/sendFile";
import useFetch from "../../../hooks/useFetch";
// component imports
import ChatPrompt from "./ChatPrompt";

import XynersysIcon from "../../../assets/reveai_light_theme.png";

function InlineImage({ fullPath, filename }) {
  const [src, setSrc] = useState(null);
  const [fetchWrapper] = useFetch();

  useEffect(() => {
    fetchWrapper({
      endpoint_url: "file_operation/getImage",
      params: { full_path: fullPath },
      method: "GET",
      json_output: false,
      set_content_type_header: false,
      onSuccess: (response) => {
        response.blob().then((blob) => setSrc(URL.createObjectURL(blob)));
      },
    });
  }, [fullPath]);

  function handleDownload() {
    const a = document.createElement("a");
    a.href = src;
    a.download = filename;
    a.click();
  }

  if (!src) return <LoadingOutlined />;
  return (
    <div style={{ display: "inline-block", marginTop: 8 }}>
      <Image
        src={src}
        alt={filename}
        style={{
          maxWidth: "100%",
          borderRadius: 8,
          display: "block",
          cursor: "pointer",
        }}
        preview={{
          toolbarRender: () => (
            <Space size={12}>
              <Tooltip title="Download">
                <DownloadOutlined
                  style={{ fontSize: 18, color: "#fff", cursor: "pointer" }}
                  onClick={handleDownload}
                />
              </Tooltip>
            </Space>
          ),
        }}
      />
    </div>
  );
}

export default function ChatBubbles({
  messageList,
  historyLoading,
  changeConversation,
  handleRefreshHistory,
  handleSend,
}) {
  const { sendFile } = useSendFile();
  const { token } = theme.useToken();

  const md = MarkdownIt({
    html: true,
    breaks: true,
    linkify: true,
    typographer: true,
  });
  const renderMarkdown = (content) => {
    if (typeof content !== "string") return content;

    let textContent = content;
    const extraComponents = [];

    // Handle |ACTION_IMAGE|full_path|filename|
    const imageRegex = /\|ACTION_IMAGE\|([^|]+)\|([^|]+)\|/g;
    let imageMatch;
    while ((imageMatch = imageRegex.exec(content)) !== null) {
      const fullPath = imageMatch[1];
      const filename = imageMatch[2];
      extraComponents.push(
        <InlineImage key={fullPath} fullPath={fullPath} filename={filename} />,
      );
    }
    textContent = textContent.replace(imageRegex, "");

    // Handle |ACTION_DOWNLOAD|folder_path|filename|
    const downloadRegex = /\|ACTION_DOWNLOAD\|([^|]+)\|([^|]+)\|/g;
    let downloadMatch;
    while ((downloadMatch = downloadRegex.exec(content)) !== null) {
      const folderPath = downloadMatch[1];
      const fileName = downloadMatch[2];
      extraComponents.push(
        <Card
          key={folderPath + fileName}
          size="small"
          style={{
            margin: "10px 0",
            borderRadius: 8,
            background: token.colorBgTextActive,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Space
            align="center"
            style={{ width: "100%", justifyContent: "space-between" }}
          >
            <Space>
              <FileExcelOutlined style={{ fontSize: 24, color: "#1d6f42" }} />
              <div>
                <Typography.Text
                  strong
                  style={{ display: "block", fontSize: "13px" }}
                >
                  {fileName}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: "11px" }}>
                  Excel Spreadsheet
                </Typography.Text>
              </div>
            </Space>
            <Button
              type="primary"
              shape="round"
              icon={<DownloadOutlined />}
              onClick={() => sendFile(folderPath, fileName)}
              size="small"
            >
              Download
            </Button>
          </Space>
        </Card>,
      );
    }
    textContent = textContent.replace(downloadRegex, "");

    return (
      <Typography>
        <div
          dangerouslySetInnerHTML={{ __html: md.render(textContent) }}
          className="markdown-content"
        />
        {extraComponents}
      </Typography>
    );
  };

  const BUBBLE_ROLE = {
    ai: {
      placement: "start",
      avatar: {
        src: (
          <img src={XynersysIcon} style={{ width: "50%", height: "75%" }}></img>
        ),
        style: { background: "black" },
      },
      messageRender: renderMarkdown,
      className: "ai-bubble-item",
    },
    user: {
      placement: "end",
      avatar: {
        icon: "DM",
        style: { background: token["blue-6"], fontSize: "14px" },
      },
      messageRender: renderMarkdown,
      className: "user-bubble-item",
    },
    file: {
      avatar: { icon: <FileOutlined /> },
      messageRender: (items) => (
        <Flex style={{ width: "100%" }}>
          {items.map((item) => (
            <Attachments.FileCard key={item.uid} item={item} />
          ))}
        </Flex>
      ),
      className: "file-bubble-item",
    },
  };

  const listRef = useRef(null);
  const showMessage = useShowMessage();

  async function handleCopy(content) {
    try {
      if (typeof content == "string") {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(content);
          showMessage({
            type: "success",
            message: "Content copied to clipboard!",
          });
        } else {
          console.log("Using fallback method to copy.");

          const textArea = document.createElement("textarea");
          textArea.value = content;

          textArea.style.position = "absolute";
          textArea.style.left = "-9999px";
          document.body.appendChild(textArea);

          textArea.focus();
          textArea.select();

          try {
            document.execCommand("copy");
            showMessage({
              type: "success",
              message: "Content copied to clipboard (fallback)!",
            });
          } catch (err) {
            showMessage({ message: "Failed to copy content (fallback)" });
          }
        }
      } else {
        showMessage({ message: "No content to copy" });
      }
    } catch (err) {
      showMessage({ message: "Failed to copy content" });
    }
  }

  function handleActionClick(item) {
    if (item.key == "copy") {
      handleCopy(item["item"]["message"]);
    } else if (item.key == "reload") {
      handleRefreshHistory();
    }
  }

  return messageList.length > 0 ? (
    <Bubble.List
      ref={listRef}
      className="custom-bubble-list"
      items={messageList.map((item, index) => {
        let defaultDict = {
          key: item.id,
          role: item?.file ? "file" : item.status == "local" ? "user" : "ai",
          loading:
            (item.status == "loading" && item.message == undefined) ||
            (item.status == "loading" &&
              item.message != undefined &&
              item.message.endsWith("Loading...")),
        };

        if (index == messageList.length - 1 && item["status"] == "loading") {
          defaultDict["typing"] = { step: 5, interval: 5 };
        }

        let actionList = [];
        if (typeof item.message == "string" && typeof item.id != "string") {
          actionList.push({
            key: "copy",
            icon: <CopyOutlined />,
            label: "Copy",
            message: item.message,
          });
        }

        if (item.status == "error") {
          defaultDict["content"] = (
            <Typography.Text type="danger">{item.message}</Typography.Text>
          );
          actionList.push({
            key: "reload",
            icon: <ReloadOutlined />,
            label: "Reload",
          });
        } else defaultDict["content"] = item.message;

        if (item?.file) {
          defaultDict["content"] = item.file;
          defaultDict["placement"] = "end";
        }

        if (item?.file == undefined)
          defaultDict["footer"] = (
            <Flex gap={"small"}>
              <Actions items={actionList} onClick={handleActionClick} />
              {item?.["footer"] && item["footer"]}
            </Flex>
          );

        return defaultDict;
      })}
      roles={BUBBLE_ROLE}
      style={{ padding: "0px 15px" }}
    />
  ) : (
    <Flex justify="center" align="center" style={{ height: "100%" }}>
      {changeConversation && historyLoading ? (
        <LoadingOutlined />
      ) : (
        <Flex vertical gap={"large"} justify="center" align="center">
          <Welcome
            variant="borderless"
            // icon={<RobotOutlined style={{ fontSize: "64px" }} />}
            title={"Hi, How can I help you ?"}
            description={""}
          />
          <ChatPrompt handleSend={handleSend} />
        </Flex>
      )}
    </Flex>
  );
}
